"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, Calendar, CheckCircle2, Layers3, KeyRound, BarChart3, FileText } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
const highlights = [{
  icon: Layers3,
  label: "Hard caps",
  text: "No fake unlimited usage promises"
}, {
  icon: KeyRound,
  label: "BYO-Key",
  text: "LLM and OCR usage stay outside platform pricing"
}, {
  icon: BarChart3,
  label: "Predictable scaling",
  text: "Add capacity only when your deployment needs it"
}];
export default function PricingHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="pricing-hero" className="relative overflow-hidden bg-background pt-12 pb-10 lg:pt-16 lg:pb-14">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/8 via-background to-background" />
      <div className="pointer-events-none absolute right-0 top-0 h-[360px] w-[360px] rounded-full bg-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute left-0 bottom-0 h-[260px] w-[260px] rounded-full bg-primary/5 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={cn("grid items-center gap-8 lg:grid-cols-12 transition-all duration-700 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="lg:col-span-7">
            <Badge variant="outline" className="mb-6 border-border/60 bg-background/70 px-3 py-1.5 text-foreground backdrop-blur-sm">
              <Layers3 className="mr-2 size-4 text-primary" />
              RAG pricing for businesses and agencies
            </Badge>

            <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-[3.7rem] leading-[1.02]">
              Pricing for a business-ready{" "}
              <span className="text-primary">RAG platform</span>
            </h1>

            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-[1.12rem]">
              EloRAG is sold through subscriptions with hard caps, BYO-Key support,
              optional white-label, and Capacity Packs for predictable scaling across
              teams, tenants, and client environments.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="h-12 px-8 text-base shadow-sm">
                <Link to="/BookADemoContactSalesPage">
                  Book a Demo
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 bg-background/60 px-8 text-base backdrop-blur-sm">
                <Link to="/WhiteLabelForAgenciesPage">
                  <SafeHydrate><Calendar className="mr-2 size-4 text-muted-foreground" /></SafeHydrate>
                  White-Label
                </Link>
              </Button>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-medium text-muted-foreground">
              <span>Annual billing saves 15%</span>
              <span className="hidden sm:inline text-border">•</span>
              <span>No unlimited usage</span>
              <span className="hidden sm:inline text-border">•</span>
              <span>BYO-Key model</span>
              <span className="hidden sm:inline text-border">•</span>
              <span>White-label sold separately</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <Card className="rounded-2xl border border-border/70 bg-card/95 p-6 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.16)] backdrop-blur-xl">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider text-primary">
                    Pricing principles
                  </div>
                  <div className="mt-1 text-xl font-semibold text-foreground">
                    Built for cost clarity
                  </div>
                </div>
                <div className="rounded-xl bg-primary/10 p-3">
                  <CheckCircle2 className="size-6 text-primary" />
                </div>
              </div>

              <div className="space-y-3">
                {highlights.map(item => {
                const Icon = item.icon;
                return <div key={item.label} className="rounded-xl border border-border/70 bg-secondary/25 p-4">
                      <div className="flex items-start gap-3">
                        <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                          <Icon className="size-4 text-primary" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-foreground">
                            {item.label}
                          </div>
                          <div className="mt-1 text-sm leading-relaxed text-muted-foreground">
                            {item.text}
                          </div>
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              <div className="mt-4 rounded-xl border border-primary/20 bg-primary/[0.04] p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <FileText className="size-4 text-primary" />
                  What this means
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  EloRAG charges for the platform layer. Variable LLM and OCR usage
                  stay outside the subscription and are billed through the customer’s
                  chosen providers.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}